import { useEffect, useRef } from "react";
import { Phone, Mail, Globe, MapPin, Building2, Wrench, Settings, Thermometer, Wind, Gauge, Power, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow, SlideShell, SectionHead } from "./shared";

const SERVICE_ICONS = [Wrench, Settings, Thermometer, Wind, Gauge, Power];

export const ServicesSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-white">
    <div data-testid="slide-services">
      <SectionHead slide={slide} isPrint={isPrint} compact />
      <div className="mt-6 grid md:grid-cols-3 gap-3">
        {(slide.items || []).map((it, i) => {
          const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
          const featured = i === 0;
          const wide = i === slide.items.length - 1 && slide.items.length % 3 === 0;
          return (
            <Reveal key={i} isPrint={isPrint} delay={0.12 + i * 0.07} className={featured ? "md:col-span-2" : wide ? "md:col-span-3" : ""}>
              <div
                className={`group h-full rounded-xl p-4 lg:p-5 border transition-colors duration-300 ${
                  featured
                    ? "bg-graphite border-graphite text-white"
                    : "bg-white border-line hover:border-brand/50"
                } ${wide ? "md:flex md:items-center md:gap-6" : ""}`}
              >
                <div className="flex items-start justify-between shrink-0">
                  <span className="inline-flex p-2 rounded-lg bg-brand text-white">
                    <Icon size={18} strokeWidth={1.8} />
                  </span>
                  <span className={`font-mono text-xs ${featured ? "text-white/30" : "text-slate-300"}`}>
                    S.{String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className={wide ? "md:flex md:items-baseline md:gap-4 md:flex-1 md:min-w-0" : ""}>
                  <h3
                    className={`mt-2.5 font-display font-bold tracking-tight ${
                      featured ? "text-xl lg:text-2xl text-white" : "text-lg text-ink"
                    } ${wide ? "md:mt-0 md:shrink-0" : ""}`}
                  >
                    {it.title}
                  </h3>
                  <p className={`mt-1 text-sm leading-relaxed ${featured ? "text-white/60" : "text-slate-500"} ${wide ? "md:mt-0 md:truncate" : ""}`}>
                    {it.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </SlideShell>
);

export const StepsSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-mist bg-blueprint-light">
    <div data-testid="slide-process">
      <SectionHead slide={slide} isPrint={isPrint} />
      <div className="mt-14 relative grid md:grid-cols-3 gap-12 md:gap-8">
        <div className="hidden md:block absolute top-7 left-[18%] right-[18%] h-px bg-slate-300" />
        {(slide.items || []).map((s, i) => (
          <Reveal key={i} isPrint={isPrint} delay={0.15 + i * 0.12} className="relative">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-ink flex items-center justify-center font-mono font-bold text-ink relative z-10">
              {String(i + 1).padStart(2, "0")}
              <span className="absolute -right-1 -top-1 w-3 h-3 bg-brand rounded-full" />
            </div>
            <h3 className="mt-6 font-display font-bold text-xl lg:text-2xl text-ink tracking-tight">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-xs">{s.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);

export const NumbersSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-white">
    <div data-testid="slide-pillars">
      <SectionHead slide={slide} isPrint={isPrint} />
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
        {(slide.items || []).map((it, i) => (
          <Reveal key={i} isPrint={isPrint} delay={0.12 + i * 0.08}>
            <div className="font-display font-extrabold text-6xl lg:text-7xl tracking-tight text-outline select-none leading-none">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="mt-4 w-8 h-1 bg-brand" />
            <h3 className="mt-4 font-display font-bold text-xl text-ink tracking-tight">{it.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{it.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);

export const SectorsSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-mist">
    <div data-testid="slide-sectors">
      <SectionHead slide={slide} isPrint={isPrint} />
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(slide.items || []).map((it, i) => (
          <Reveal key={i} isPrint={isPrint} delay={0.12 + i * 0.08} className="h-full">
            <div className="group h-full bg-white border border-line rounded-xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-slate-200">
              {it.image && (
                <div className="h-36 lg:h-40 overflow-hidden">
                  <img
                    src={it.image}
                    alt={it.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                  {String(i + 1).padStart(2, "0")} / SETTORE
                </div>
                <h3 className="mt-2 font-display font-bold text-lg text-ink tracking-tight leading-snug">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{it.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);

export const GallerySlide = ({ slide, isPrint }) => {
  const trackRef = useRef(null);
  const paused = useRef(false);
  const resumeTimer = useRef(null);
  const drag = useRef(null);
  const items = slide.items || [];

  useEffect(() => {
    if (isPrint) return;
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!paused.current && !drag.current) {
        el.scrollLeft += 0.6;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [isPrint, items.length]);

  const pauseTemporarily = () => {
    paused.current = true;
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => (paused.current = false), 4000);
  };

  const scrollByDir = (dir) => {
    pauseTemporarily();
    trackRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, left: trackRef.current.scrollLeft };
    pauseTemporarily();
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    trackRef.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
  };
  const onPointerUp = () => (drag.current = null);

  if (isPrint) {
    return (
      <SlideShell className="bg-mist">
        <div data-testid="slide-gallery">
          <SectionHead slide={slide} isPrint compact />
          <div className="mt-8 grid grid-cols-3 gap-3">
            {items.slice(0, 6).map((it, i) => (
              <div key={i}>
                <img src={it.image} alt={it.title} className="w-full h-44 object-cover rounded-lg" />
                <div className="mt-2 font-display font-semibold text-sm text-ink">{it.title}</div>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    );
  }

  return (
    <SlideShell className="bg-mist bg-blueprint-light">
      <div data-testid="slide-gallery">
        <div className="flex items-end justify-between gap-6">
          <SectionHead slide={slide} isPrint={isPrint} compact />
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              data-testid="gallery-prev-button"
              title="Scorri indietro"
              onClick={(e) => {
                e.currentTarget.blur();
                scrollByDir(-1);
              }}
              className="p-3 rounded-full border border-line bg-white text-ink hover:border-brand hover:text-brand transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="gallery-next-button"
              title="Scorri avanti"
              onClick={(e) => {
                e.currentTarget.blur();
                scrollByDir(1);
              }}
              className="p-3 rounded-full border border-line bg-white text-ink hover:border-brand hover:text-brand transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        <div className="relative left-1/2 -translate-x-1/2 w-screen mt-8">
          <Reveal isPrint={isPrint} delay={0.2}>
            <div
              ref={trackRef}
              data-testid="gallery-track"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              onMouseEnter={pauseTemporarily}
              className="flex gap-4 overflow-x-auto no-scrollbar px-6 sm:px-12 lg:px-20 xl:px-28 cursor-grab active:cursor-grabbing select-none"
            >
              {items.concat(items).map((it, i) => (
                <figure key={i} className="group relative shrink-0 w-72 sm:w-96 rounded-xl overflow-hidden border border-line bg-white">
                  <img
                    src={it.image}
                    alt={it.title}
                    draggable="false"
                    className="w-full h-56 sm:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#090A0C]/85 to-transparent px-4 pt-10 pb-3.5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                      IMP.{String((i % items.length) + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display font-semibold text-white text-sm sm:text-base leading-snug">{it.title}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </SlideShell>
  );
};

export const MetricsSlide = ({ slide, isPrint }) => (
  <div
    className="relative h-[100dvh] w-full overflow-hidden bg-graphite text-white flex flex-col justify-center"
    data-testid="slide-metrics"
  >
    <div className="absolute inset-0 bg-blueprint" />
    <div className="absolute inset-0 bg-gradient-to-b from-graphite/60 via-transparent to-graphite/80" />
    <div className="relative px-6 sm:px-12 lg:px-20 xl:px-28 w-full max-w-7xl mx-auto py-24">
      <SectionHead slide={slide} isPrint={isPrint} dark />
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden">
        {(slide.metrics || []).map((m, i) => (
          <Reveal key={i} isPrint={isPrint} delay={0.15 + i * 0.1} className="bg-graphite/90 p-6 lg:p-8">
            <div className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#EF4444] leading-none">
              {m.value}
            </div>
            <div className="mt-4 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50 leading-relaxed">
              {m.label}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

export const ContactSlide = ({ slide, isPrint }) => {
  const rows = [
    { icon: Building2, label: "Ragione sociale", value: slide.company },
    { icon: Phone, label: "Telefono", value: slide.phone },
    { icon: Mail, label: "Email", value: slide.email },
    { icon: Globe, label: "Web", value: slide.website },
    { icon: MapPin, label: "Sede", value: slide.address },
  ].filter((r) => r.value);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-graphite" data-testid="slide-contact">
      {slide.image && (
        <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-[#090A0C] via-[#090A0C]/80 to-[#090A0C]/40" />
      <div className="relative h-full flex flex-col justify-center px-6 sm:px-12 lg:px-20 xl:px-28 py-24">
        <div className="max-w-7xl mx-auto w-full">
          <Reveal isPrint={isPrint}>
            <Eyebrow dark>{slide.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal isPrint={isPrint} delay={0.1}>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              {slide.title}
            </h2>
          </Reveal>
          <Reveal isPrint={isPrint} delay={0.2}>
            <p className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl">{slide.subtitle}</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-6 max-w-2xl">
            {rows.map((r, i) => (
              <Reveal key={i} isPrint={isPrint} delay={0.3 + i * 0.07} className="flex items-center gap-4">
                <span className="inline-flex p-3 rounded-lg border border-white/20 text-white shrink-0">
                  <r.icon size={18} strokeWidth={1.8} />
                </span>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{r.label}</div>
                  <div className="font-display font-semibold text-white leading-snug">{r.value}</div>
                </div>
              </Reveal>
            ))}
          </div>
          {!isPrint && slide.cta && (
            <Reveal delay={0.6}>
              <a
                data-testid="contact-cta-button"
                href={`mailto:${slide.email}`}
                className="mt-10 inline-flex items-center gap-3 bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm uppercase tracking-widest px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                {slide.cta}
              </a>
            </Reveal>
          )}
        </div>
      </div>
    </div>
  );
};
